import { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatDateTime } from '../../utils/dateFormatter';
import { comprobanteSchema, productoSchema } from '../../validation/addProductsFormsValidations';
import { toTitleCase, toSentenceCase } from '../../utils/textFormatter';
import './agregar-producto.css';
import { buscarProducto } from '../../services/inventarioService'

export default function AgregarProductoModal({ visible, onHide, onSubmit }) {
  const [productos, setProductos] = useState([]);
  const [productoEditandoIndex, setProductoEditandoIndex] = useState(null);
  const [buscandoProd, setBuscandoProd] = useState(false);
  const [usarCodigoFijo, setUsarCodigoFijo] = useState(false);
  const unidades = ['Kg', 'g', 'L', 'ml', 'cc', 'mt', 'cm', 'un', 'pc'];
  const bodegas = ['Bodega 1', 'Bodega 2', 'Bodega 3'];
  const toast = useRef(null);
  
  // Inicialización formulario de comprobante
  const { 
    register, handleSubmit, reset, watch, formState: { errors }
  } = useForm({ 
    resolver: yupResolver(comprobanteSchema), 
    defaultValues: { tipo: 'Factura' },
    mode: 'onTouched'
  });
  const tipoComprobante = watch('tipo');

  // Inicialización formulario de producto
  const { 
    register: registerProd, 
    handleSubmit: handleSubmitProd,
    reset: resetProd,
    formState: { errors: errorsProd },
    setValue: setValueProd,
    clearErrors, 
  } = useForm({ 
    resolver: yupResolver(productoSchema),
    defaultValues: { cantidad: 1, precio: 0, cuentaContable: 'Existencia', codigoBarra: ''},
    mode: 'onTouched'
  });

  const handleAgregarProducto = (productoData) => {
    const nuevoProducto = {
      nombre: toTitleCase(productoData.nombre),
      descripcion: toSentenceCase(productoData.descripcion),
      codigoBarra: productoData.codigoBarra,
      marca: toTitleCase(productoData.marca),
      medida: Number(productoData.medida),
      unidad: productoData.unidad,
      cantidad: Number(productoData.cantidad),
      precio: Number(productoData.precio),
      codigoCode: productoData.codigoCode,
      codigoOem: productoData.codigoOem,
      bodega: productoData.bodega,
      cuentaContable: productoData.cuentaContable,
    };
    
    if (tipoComprobante !== 'Sin Doc' && productoData.codigoBarra === 'NO-BARCODE') {
      toast.current.show({ 
        severity: 'error', 
        summary: 'Error',
        detail: 'El código "NO-BARCODE" solo se permite cuando el comprobante es "Sin Documento"',
        life: 4000
      }); return;
    }

    if (productoEditandoIndex !== null) {
      // Actualiza producto existente
      const actualizados = [...productos];
      actualizados[productoEditandoIndex] = nuevoProducto;
      setProductos(actualizados);
      setProductoEditandoIndex(null);
    } else {
      // Agrega nuevo producto
      setProductos([...productos, nuevoProducto]);
    }
    resetProd();
    setUsarCodigoFijo(false);
  };

  const handleConfirmar = (comprobanteData) => {
    const totalNeto = calcularTotalNeto(productos);

    const comprobanteFinal = 
    comprobanteData.tipo === 'Sin Doc' ? {
      ...comprobanteData,
      tipo: 'Sin Doc',
      nombreEmpresa: 'Sin Documento',
      rutEmpresa: '00000000-0',
      codigo: 'NO-DOC-CODE',
      precioNeto: totalNeto,
      fecha: formatDateTime(new Date()),
    } : {
      ...comprobanteData,
      nombreEmpresa: toTitleCase(comprobanteData.nombreEmpresa),
      fecha: formatDateTime(comprobanteData.fecha),
    };

    /**
     * Verifica si total de factura es igual a la suma de los productos
     * Alerta mostrando la diferencia en caso de que no coincidan.
     * */
    const { valido, diferencia } = validarTotalNeto(comprobanteFinal, productos);
    if(!valido) {
      toast.current.show({ 
        severity: 'error', 
        summary: 'Total Neto Incorrecto',
        detail: `El total neto no coincide. Diferencia: ${Math.abs(diferencia).toLocaleString('es-CL', {  style: 'currency', currency: 'CLP' })}`,
        life: 4000
      }); return;
    }

    /**
     * Verifica si hay algun producto que tenga 'NO-BARCODE' 
     * cuando se trata de un comprobante distinto de 'Sin Documento'.
     */
    if (comprobanteData.tipo !== 'Sin Doc') {
      const tieneNoBarCode = productos.some(
        (prod) => prod.codigoBarra === 'NO-BARCODE'
      );
      if (tieneNoBarCode) {
        toast.current.show({ 
          severity: 'error', 
          summary: 'Error',
          detail: 'El código "NO-BARCODE" solo se permite cuando el comprobante es "Sin Documento"',
          life: 6000
        }); return;
      }
    }

    // Envia los datos
    onSubmit({ comprobante: comprobanteFinal, productos });
    // Limpia los formularios
    reset();
    resetProd();
    setProductos([]);
    onHide();
  };

  /**
   * Valida que el total neto de la factura/boleta sea igual al 
   * total de la suma de los precios unitarios de los productos.
   * */
  const validarTotalNeto = (comprobanteData, productos) => {
    // Obtener el total neto de la factura
    const totalNeto = comprobanteData.precioNeto;
    // Obtener la suma de los productos
    const sumaProductos = calcularTotalNeto(productos);
    // Obtener la diferencia
    const diferencia =  totalNeto - sumaProductos;
    // Comparar ambos valores
    if (Math.abs(diferencia) > 5) {
      return { valido: false, diferencia: diferencia };
    } return { valido: true, diferencia: 0 };
  }

  /**
   * Itera sobre la lista de productos sumando el precio unitario
   * por la cantidad de cada uno para obtener el total.
   */
  const calcularTotalNeto = (productos) => {
    const result = productos.reduce((totalAcumulado, producto) => 
      totalAcumulado + ( parseFloat(producto.precio) * parseFloat(producto.cantidad) ), 0);
    return result
  };

  const renderFooter = () => (
    <div className="inv-btns-container">
      <Button 
        type="button" 
        label="Limpiar Todo" 
        icon="pi pi-eraser"
        className="inv-btn-cancelar"
        onClick={() => {
          reset();
          resetProd();
          setProductos([]);
        }}
      />
      <Button 
        type="button" 
        label="Confirmar" 
        icon="pi pi-check" 
        className="inv-btn-confirm" 
        onClick={() => handleSubmit(handleConfirmar)()} 
        disabled={productos.length === 0}
      />
    </div>
  );

  const autocompletarProducto = async (codigo) => {
    if (!codigo || usarCodigoFijo) return;
    if (/^NO[-.]?BARCODE$/i.test(codigo)) return;

    try {
      setBuscandoProd(true);
      const res = await buscarProducto(codigo.trim());

      if (!res.ok) {
        toast.current?.show({
          severity: res.code === 'NOT_FOUND' ? 'warn' : 'error',
          summary: res.code === 'NOT_FOUND' ? 'No encontrado' : 'Error',
          detail: res.message,
          life: 3500
        });
        return;
      }

      const p = res.data;
      setValueProd('nombre', toTitleCase(p.nombre || ''));
      setValueProd('descripcion', toSentenceCase(p.descripcion || ''));
      setValueProd('marca', toTitleCase(p.marca || 'Sin Marca'));
      setValueProd('medida', p.medida ?? '');
      setValueProd('unidad', p.unidad ?? '');
      setValueProd('precio', p.precio ?? 0);
      setValueProd('codigoCode', p.codigoCode == null ? '' : p.codigoCode);
      setValueProd('codigoOem', p.codigoOem ?? '');
      setValueProd('bodega', p.bodega ?? 'Bodega 1');
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error || 'No se pudo consultar el producto.',
        life: 4000
      });
    } finally {
      setBuscandoProd(false);
    }
  };


  useEffect(() => {
    if (tipoComprobante !== 'Sin Doc') {
      setUsarCodigoFijo(false);
      setValueProd('codigoBarra', '');
    } else if (usarCodigoFijo) {
      setValueProd('codigoBarra', usarCodigoFijo ? 'NO-BARCODE' : '');
      if (usarCodigoFijo) clearErrors('codigoBarra');
    }
  }, [tipoComprobante, usarCodigoFijo, setValueProd, clearErrors]);

  return (
    <div>
      <Toast ref={toast} position='center'/>
      <Dialog 
        header="AGREGAR PRODUCTOS"
        className="dialog-container" 
        visible={visible} blockScroll
        style={{ width: '95vw', maxWidth: '1200px' }}  
        onHide={onHide}
        position="center" modal 
        footer={renderFooter()}
      >

        <div className='div-form-add-product'>
          {/* Sección comprobante */}
          <div className='info-comprobante'>
            <h5>Información del Comprobante</h5>
            <form>
              <div className='inv-campo-tipo-comprobante'>
                <label className="mr-2">
                  Tipo de comprobante<span className="required">*</span>
                </label>
                <div>
                  <label>
                    <input type="radio" className="form-check-input"
                    value="Boleta" {...register('tipo')}/> Boleta
                  </label>
                </div>
                <div>
                  <label>
                    <input type="radio" className="form-check-input" 
                    value="Factura" {...register('tipo')}/> Factura
                  </label>
                </div>
                <div>
                  <label>
                    <input type="radio" className="form-check-input" 
                    value="Sin Doc" {...register('tipo')}/> Sin Documento
                  </label> 
                </div>
              </div>

              { tipoComprobante !== 'Sin Doc' && (
                <div className='campos-comprobante-container'>

                  <div className="form-comprobante-row">
                    <div className="mb-2 form-comprobante-field">
                      <label className="form-label">
                        Nombre Empresa<span className="required">*</span>
                      </label>
                      <input type="text"
                        placeholder='Ingrese nombre de la empresa'
                        className={`form-control ${errors.nombreEmpresa ? 'is-invalid' : ''}`}
                        {...register('nombreEmpresa')}
                      />
                      {errors.nombreEmpresa && (
                        <div className="invalid-feedback">{errors.nombreEmpresa.message}</div>
                      )}
                    </div>
                    <div className="mb-2 form-comprobante-field">
                      <label className="form-label">
                        RUT Empresa<span className="required">*</span>
                      </label>
                      <input type="text" placeholder='Ingrese RUT de la empresa'
                        className={`form-control ${errors.rutEmpresa ? 'is-invalid' : ''}`}
                        {...register('rutEmpresa')}
                      />
                      {errors.rutEmpresa && (
                        <div className="invalid-feedback">{errors.rutEmpresa.message}</div>
                      )}
                    </div>
                  </div>

                  <div className="form-comprobante-row">
                    <div className="mb-2 form-comprobante-field">
                      <label className="form-label">
                        N° de Comprobante<span className="required">*</span>
                      </label>
                      <input type="text" placeholder='N° de comprobante'
                        className={`form-control ${errors.codigo ? 'is-invalid' : ''}`}
                        {...register('codigo')}
                      />
                      {errors.codigo && (
                        <div className="invalid-feedback">{errors.codigo.message}</div>
                      )}
                    </div>
                    <div className="mb-2 form-comprobante-field">
                      <label className="form-label">
                        Fecha de Emisión<span className="required">*</span>
                      </label>
                      <input type='date'
                        placeholder='Fecha de emisión del comprobante'
                        className={`form-control ${errors.fecha ? 'is-invalid' : ''}`}
                        {...register('fecha')}
                      />
                      {errors.fecha && (
                        <div className="invalid-feedback">{errors.fecha.message}</div>
                      )}
                    </div>
                    <div className="mb-2 form-comprobante-field">
                      <label className="form-label">
                        <b>Precio Total Neto</b><span className="required">*</span>
                      </label>
                      <input type='text'
                        placeholder='Precio total neto'
                        className={`form-control ${errors.precioNeto ? 'is-invalid' : ''}`}
                        {...register('precioNeto')}
                      />
                      {errors.precioNeto && (
                        <div className="invalid-feedback">{errors.precioNeto.message}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Sección productos */}
          <div className="productos-layout">
            {/* Agregar producto */}
            <div className='info-producto'>
              <h5>{productoEditandoIndex !== null ? 'Modo Edición' : 'Agregar Producto'}</h5>
              <form onSubmit={handleSubmitProd(handleAgregarProducto)} className="form-add-producto">
                <div className="campos-add-container">
                  
                  <div className="mb-2">
                    <label className="form-label">
                      Código de Barra<span className="required">*</span>
                    </label>
                    <input 
                      type="text" placeholder='Ingrese código de barra del producto'
                      className={`form-control ${errorsProd.codigoBarra ? 'is-invalid' : ''}`}
                      {...registerProd('codigoBarra')}
                      disabled={usarCodigoFijo || buscandoProd}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          const codigo = e.currentTarget.value;
                          await autocompletarProducto(codigo);
                        }
                      }}
                    />
                    {buscandoProd && <small className="text-muted">Buscando…</small>}
                    {errorsProd.codigoBarra && (
                      <div className="invalid-feedback">{errorsProd.codigoBarra.message}</div>
                    )}
                    {tipoComprobante === 'Sin Doc' && (
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="usarCodigoFijo"
                          checked={usarCodigoFijo}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setUsarCodigoFijo(checked);
                            setValueProd('codigoBarra', checked ? 'NO-BARCODE' : '');
                          }}
                        />
                        <label className="form-check-label" htmlFor="usarCodigoFijo">
                          Sin código de barra
                        </label>
                      </div>
                    )}
                  </div>

                  <div className="mb-2">
                    <label className="form-label">
                      Nombre Producto<span className="required">*</span>
                    </label>
                    <input type="text" placeholder='Ingrese nombre del producto'
                      className={`form-control ${errorsProd.nombre ? 'is-invalid' : ''}`}
                      {...registerProd('nombre')}
                    />
                    {errorsProd.nombre && ( <div className="invalid-feedback">{errorsProd.nombre.message}</div> )}
                  </div>

                  <div className="mb-2">
                    <label className="form-label">
                      Descripción Producto
                    </label>
                    <input type="text" placeholder='Ingrese descripción del producto'
                      className={`form-control ${errorsProd.descripcion ? 'is-invalid' : ''}`}
                      {...registerProd('descripcion')}
                    />
                    {errorsProd.descripcion && (
                      <div className="invalid-feedback">{errorsProd.descripcion.message}</div>
                    )}
                  </div>

                  <div className="mb-2">
                    <label className="form-label">
                      Marca Producto<span className="required">*</span>
                    </label>
                    <input type="text" placeholder='Ingrese marca del producto / Sin Marca'
                      className={`form-control ${errorsProd.marca ? 'is-invalid' : ''}`}
                      {...registerProd('marca')}
                    />
                    {errorsProd.marca && (
                      <div className="invalid-feedback">{errorsProd.marca.message}</div>
                    )}
                  </div>

                  <div className="mb-2">
                    <label className="form-label">
                      Unidad del Producto<span className="required">*</span>
                    </label>
                    <div className="campo-unidad-wrap">
                      <div className="unidad-input-wrap">
                        <input type="text"
                          placeholder='Medida (Ej. 2)'
                          className={`form-control ${errorsProd.medida ? 'is-invalid' : ''}`}
                          {...registerProd('medida')}
                        />
                        {errorsProd.medida && (<div className="invalid-feedback d-block">{errorsProd.medida.message}</div>)}
                      </div>
                      <div className="unidad-input-wrap">
                        <select 
                          {...registerProd('unidad')} 
                          className={`form-select ${errorsProd.unidad ? 'is-invalid' : ''}`}
                          >
                          <option value="">Elegir unidad</option>
                          {unidades.map((unidad) => (
                            <option key={unidad} value={unidad}>{unidad}</option>
                          ))}
                        </select>
                        {errorsProd.unidad && (<div className="invalid-feedback d-block">{errorsProd.unidad.message}</div>)}
                      </div>
                    </div>
                  </div>

                  <div className="campo-unidad-wrap">
                    <div className="unidad-input-wrap mb-2">
                      <label className="form-label">
                        Cantidad<span className="required">*</span>
                      </label>
                      <input type="text"
                        placeholder='Cantidad del producto'
                        className={`form-control ${errorsProd.cantidad ? 'is-invalid' : ''}`}
                        {...registerProd('cantidad')}
                      />
                      {errorsProd.cantidad && (
                        <div className="invalid-feedback">{errorsProd.cantidad.message}</div>
                      )}
                    </div>

                    <div className="unidad-input-wrap mb-2">
                      <label className="form-label">
                        Precio Unitario<span className="required">*</span>
                      </label>
                      <input type="text"
                        placeholder='Precio unitario del producto'
                        className={`form-control ${errorsProd.precio ? 'is-invalid' : ''}`}
                        {...registerProd('precio')}
                      />
                      {errorsProd.precio && (
                        <div className="invalid-feedback">{errorsProd.precio.message}</div>
                      )}
                    </div>
                  </div>

                  <div className="campo-unidad-wrap">
                    <div className="unidad-input-wrap mb-2">
                      <label className="form-label">
                        Código CODE
                      </label>
                      <input type="text" placeholder='Ingrese CODE'
                        className={`form-control ${errorsProd.codigoCode ? 'is-invalid' : ''}`}
                        {...registerProd('codigoCode')}
                      />
                      {errorsProd.codigoCode && (
                        <div className="invalid-feedback">{errorsProd.codigoCode.message}</div>
                      )}
                    </div>

                    <div className="unidad-input-wrap mb-2">
                      <label className="form-label">
                        Código OEM
                      </label>
                      <input type="text" placeholder='Ingrese OEM'
                        className={`form-control ${errorsProd.codigoOem ? 'is-invalid' : ''}`}
                        {...registerProd('codigoOem')}
                      />
                      {errorsProd.codigoOem && (
                        <div className="invalid-feedback">{errorsProd.codigoOem.message}</div>
                      )}
                    </div>
                  </div>

                  <div className="campo-unidad-wrap">
                    <div className="unidad-input-wrap mb-2">
                      <label className="form-label">
                        Bodega<span className="required">*</span>
                      </label>
                      <select 
                        {...registerProd('bodega')} 
                        className={`form-select ${errorsProd.bodega ? 'is-invalid' : ''}`}
                        >
                        <option value="">Elegir bodega</option>
                        {bodegas.map((bodega) => (
                          <option key={bodega} value={bodega}>{bodega}</option>
                        ))}
                      </select>
                      {errorsProd.bodega && (<div className="invalid-feedback d-block">{errorsProd.bodega.message}</div>)}
                    </div>

                    <div className="unidad-input-wrap mb-2">
                      <label className="form-label">
                        Cuenta Contable<span className="required">*</span>
                      </label>
                      <input type="text" 
                        placeholder='Ingrese cuenta contable' disabled
                        className={`form-control ${errorsProd.cuentaContable ? 'is-invalid' : ''}`}
                        {...registerProd('cuentaContable')}
                      />
                      {errorsProd.cuentaContable && (
                        <div className="invalid-feedback">{errorsProd.cuentaContable.message}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className='inv-btns-container mt-3'>
                  {productoEditandoIndex !== null && (
                    <Button icon="pi pi-times" label="Cancelar" 
                      className="inv-btn-cancelar"
                      onClick={() => {
                        resetProd();
                        setProductoEditandoIndex(null);
                      }}
                    />
                  )}

                  <Button type="submit"
                    icon={productoEditandoIndex !== null ? 'pi pi-check' : 'pi pi-plus'}
                    label={productoEditandoIndex !== null ? 'Actualizar' : 'Agregar'} 
                    className="inv-btn-add"
                  />
                </div>
              </form>
            </div>

            {/* Lista de productos */}
            <div className="lista-producto">
              <h5>Productos Agregados</h5>

              {productos.length === 0 ? (
                <p>No hay productos agregados.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Producto</th>
                        <th>Unidad de Medida</th>
                        <th>Cantidad</th>
                        <th>Precio</th>
                        <th>Total</th>
                        <th>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productos.map((prod, idx) => (
                        <tr 
                          key={idx}
                          className={productoEditandoIndex === idx ? 'table-primary' : ''}
                          onClick={() => {
                            const producto = productos[idx];
                            setValueProd('nombre', producto.nombre);
                            setValueProd('descripcion', producto.descripcion);
                            setValueProd('codigoBarra', producto.codigoBarra); 
                            setValueProd('codigoCode', producto.codigoCode); 
                            setValueProd('codigoOem', producto.codigoOem); 
                            setValueProd('marca', producto.marca);
                            setValueProd('medida', producto.medida);
                            setValueProd('unidad', producto.unidad);
                            setValueProd('cantidad', producto.cantidad);
                            setValueProd('precio', producto.precio);
                            setValueProd('bodega', producto.bodega);

                            // Marca "Sin código de barra" si corresponde
                            const esNoBarcode = /^NO[-.]BARCODE$/i.test(String(producto.codigoBarra || '').trim());
                            setUsarCodigoFijo(esNoBarcode);
                            if (esNoBarcode) setValueProd('codigoBarra', 'NO-BARCODE');

                            setProductoEditandoIndex(idx);

                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <strong>{prod.nombre}</strong><br />
                            <small className="text-muted">{prod.marca}</small>
                          </td>
                          <td>{prod.medida} {prod.unidad}</td>
                          <td>{prod.cantidad}</td>
                          <td>
                            {new Intl.NumberFormat('es-CL', {
                            style: 'currency',
                            currency: 'CLP',
                            maximumFractionDigits: 0
                          }).format(prod.precio)}</td>
                          <td>
                            {new Intl.NumberFormat('es-CL', {
                              style: 'currency',
                              currency: 'CLP',
                              maximumFractionDigits: 0
                            }).format(prod.precio * prod.cantidad)}
                          </td>
                          <td>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                const nuevaLista = productos.filter((_, i) => i !== idx);
                                setProductos(nuevaLista);
                              }}
                            >Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="4" className="text-end fw-bold">Total Final:</td>
                        <td className="fw-bold">
                          {new Intl.NumberFormat('es-CL', {
                            style: 'currency',
                            currency: 'CLP',
                            maximumFractionDigits: 0
                          }).format(
                            productos.reduce((acc, prod) => acc + prod.precio * prod.cantidad, 0)
                          )}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );

}